import { getDataLayer } from './dataLayer.js';
import { readSessionId } from './session.js';
import { getOrCreateVisitorId, getPageLoadKey } from './visitor.js';

const GA_COOKIE_NAME = '_ga';

let userId: string | null = null;
let consent = false;
let featureFlags: Record<string, boolean | string> = {};
const consentSubscribers = new Set<() => void>();
const consentChangeSubscribers = new Set<() => void>();

export function setTrackingUser(id: string | null) {
  if (userId === id) {
    return;
  }
  userId = id;
  try {
    getDataLayer()?.push({ userId: id });
  } catch {
    // dataLayer push must never break the app
  }
}

export function getSessionId(): string | null {
  if (!consent) {
    return null;
  }
  return readSessionId();
}

export function getVisitorId(): string | null {
  if (!consent) {
    return null;
  }
  return getOrCreateVisitorId();
}

/** The key a feature-flag assignment sticks to. With no key at all Unleash re-seeds on every evaluation. */
export function getStickinessKey(): string {
  return getVisitorId() ?? getPageLoadKey();
}

export function setTrackingConsent(value: boolean) {
  if (consent === value) {
    return;
  }
  consent = value;
  for (const listener of consentSubscribers) {
    listener();
  }
}

export function subscribeConsent(listener: () => void): () => void {
  consentSubscribers.add(listener);
  return () => {
    consentSubscribers.delete(listener);
  };
}

/** @internal — fired by `initTracking` on every provider notification, regardless of whether the boolean changed. */
export function _notifyConsentChange() {
  for (const listener of consentChangeSubscribers) {
    listener();
  }
}

export function subscribeConsentChange(listener: () => void): () => void {
  consentChangeSubscribers.add(listener);
  return () => {
    consentChangeSubscribers.delete(listener);
  };
}

export function getUserId(): string | null {
  return userId;
}

export function setTrackingFlags(flags: Record<string, boolean | string>) {
  featureFlags = flags;
}

export function getFlags(): Record<string, boolean | string> {
  return featureFlags;
}

export function createTrackingFlagPropagator(): (flags: Record<string, boolean | string>) => void {
  return (flags) => setTrackingFlags(flags);
}

export function getConsent(): boolean {
  return consent;
}

/** Read GA4 client_id from the `_ga` cookie. Returns `null` if consent is not given or cookie is absent. */
export function getGaClientId(): string | null {
  if (!consent) {
    return null;
  }
  try {
    const match = document.cookie.match(new RegExp(`(?:^|; )${GA_COOKIE_NAME}=GA\\d+\\.\\d+\\.([^;]+)`));
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

/** @internal — test-only reset */
export function _resetContext() {
  userId = null;
  consent = false;
  featureFlags = {};
  consentSubscribers.clear();
  consentChangeSubscribers.clear();
}
